/**
 * 内置「英语高频词 → CEFR 等级」近似分级词表（教学用）
 *
 * 用途：给英语水平预测算法提供一份零依赖、零 IO 的粗粒度分级依据。
 * 算法只做两件事：(1) 统计一段文本里各等级的覆盖率，(2) 用未收录词（超纲词）占比
 * 作为难度上界的修正项。因此本表只需要「大致正确」，不需要精确。
 *
 * ⚠️ 诚实声明（重要，不要把它当成权威数据）
 * - 本表是**教学用近似分级**，由公开高频词表（CEFR 词表、四六级核心词表、雅思/托福
 *   常见词表）人工整理与取舍而成，**不是 COCA / BNC 官方排名**，两个来源之间的一致
 *   程度不保证，词与词之间的顺序只是「大致按使用频率从高到低」。
 * - 生产环境可以由管理员导入的词表覆盖：数据库 `words` 表带 `cefr_level` 列，
 *   接口层优先读库，本文件只作为种子数据与兜底。库表存在时以库表为准。
 * - 算法只用它做粗粒度分级：单看一个词无法判断真实水平（阅读者的水平由覆盖率、
 *   句法复杂度、背景知识共同决定），不要把本表结果直接当作用户等级展示。
 *
 * 数据约定（改词表时请一并遵守，否则下面的构建函数会静默按「先到先得」处理）：
 * - 每个等级是一段**打包的字符串数组**，模块加载时按空白切分；每个等级内按
 *   「使用频率大致从高到低」排列，**顺序有意义**：调用方会用词在数组中的位置做
 *   同一等级内的更细排序（位置越靠前 = 越基础/越常用）。
 * - 只收**单个英文单词的小写 lemma**：不含短语、空格、连字符、大写与中文。
 * - 跨等级不重复，等级内也不重复（构建时先到先得，靠前的等级优先）。
 * - 覆盖以「中国大学英语学习者」为参照：A1/A2 中学基础词，B1/B2 四六级高频词，
 *   C1 考研/雅思托福偏难词，C2 少量低频学术与文学词。
 */

import { CEFR_LEVELS, type CefrLevel } from '../types/index';

/**
 * 每个 CEFR 等级对应的近似词频区间（COCA 排名口径的近似值，教学用）。
 *
 * 仅用于把「某个排名区间」映射回等级，或在展示时说明某等级的粗略位置；
 * 不是精确边界，区间之间刻意留了空隙感。C2 用 30000 作为有限上界，
 * 避免 Infinity 参与序列化（该对象可能被前端 JSON 化后展示）。
 */
export const BAND_RANK_RANGE: Readonly<Record<CefrLevel, { min: number; max: number }>> = {
  A1: { min: 1, max: 500 },
  A2: { min: 501, max: 1500 },
  B1: { min: 1501, max: 3000 },
  B2: { min: 3001, max: 6000 },
  C1: { min: 6001, max: 12000 },
  C2: { min: 12001, max: 30000 },
};

// ---------------------------------------------------------------------------
// A1（中学入门 / 功能词与最基础实词）
// ---------------------------------------------------------------------------

const A1_WORDS = (
  'the be to of and a in that have it ' +
  'for not on with he as you do at this ' +
  'but his by from they we say her she ' +
  'or an will my one all would there their ' +
  'what so up out if about who get which ' +
  'go me when make can like time no just ' +
  'him know take people into year your good ' +
  'some could them see other than then now ' +
  'look only come its over think also back ' +
  'should must may might shall cannot onto once twice yet ' +
  'almost nearly quite enough someone anyone everyone nobody something ' +
  'anything everything nothing myself yourself himself herself itself theirs ' +
  'whose whom whatever whenever wherever ' +
  'after use two how our work first well ' +
  'way even new want because any these give ' +
  'day most us is are was were am been ' +
  'has had did does doing said goes going ' +
  'man woman boy girl child children baby friend ' +
  'family mother father sister brother home house room ' +
  'door school student teacher class book name number ' +
  'word letter page paper car bus train road ' +
  'city country world place thing food water milk ' +
  'bread rice apple tea table chair bed desk ' +
  'window phone computer money shop street park hospital ' +
  'doctor music game ball film picture color morning ' +
  'hand head eye ear nose mouth face arm leg tree ' +
  'bird sea river town village hill egg meat coffee salt ' +
  'sugar soup vegetable dry wet true false real angry afraid ' +
  'job market store office worker team second ' +
  'nurse police officer soldier farmer driver singer actor player pen ' +
  'pencil notebook bag box lock wall roof third fourth ' +
  'please sorry ' +
  'afternoon evening night today tomorrow yesterday week month ' +
  'hour minute monday tuesday wednesday thursday friday saturday ' +
  'sunday spring summer autumn winter three four five six seven ' +
  'eight nine ten eleven twelve twenty hundred thousand eat drink ' +
  'sleep read write speak listen hear watch play run walk ' +
  'sit stand open close buy sell pay help love need ' +
  'try learn teach study live start stop finish begin wait ' +
  'meet ask answer call talk tell show put bring send ' +
  'find lose win keep let feel hold turn move drive ' +
  'ride fly swim sing dance cook clean wash wear cut ' +
  'draw paint jump laugh cry smile thank hello goodbye yes ' +
  // large 曾漏收（它是最基础的比较级词根：larger / largest），
  // 漏收会让 lemmaCandidates('largest') 的候选排序退化并让难度打分低估常见词。
  'big large small long short tall high low old young nice ' +
  'bad happy sad hot cold warm cool fast slow easy ' +
  'hard busy free full hungry thirsty tired sick fine ' +
  'right wrong left next last same different many much few ' +
  'little more less best better beautiful pretty dirty rich poor ' +
  'strong weak quiet loud funny here where why very too ' +
  'really always often sometimes never again soon early late together ' +
  'alone away down off under near far around before still ' +
  'already maybe okay ' +
  ''
).trim().split(/\s+/);

// ---------------------------------------------------------------------------
// A2（日常场景高频词：生活、工作、学习、身体、社会常识）
// ---------------------------------------------------------------------------

const A2_WORDS = (
  'between against among during until while since whether however although though across ' +
  'along within without toward upon beside beneath except unless plus probably ' +
  'perhaps finally certainly actually usually rather government fact sort moment sentence ' +
  'recommend session task project meeting salary goods boss ' +
  'person life lot group company state system program question problem ' +
  'case idea information business service part member line side kind ' +
  'type form area level point end top front middle center ' +
  'edge space size weight length height distance amount price cost ' +
  'value able available possible sure certain ready clear obvious common ' +
  'popular famous foreign local national public private general special main ' +
  'major simple difficult important necessary useful helpful careful dangerous safe ' +
  'various several both each every either neither another such own ' +
  'whole entire half double single add change count cover deal ' +
  'decide describe develop die drop enter expect explain express fall ' +
  'fill follow grow happen hope include increase join kill land ' +
  'lift accept allow appear arrive become believe belong break build ' +
  'carry catch cause choose continue control create deliver depend destroy ' +
  'divide doubt earn encourage enjoy examine exercise exist experience fail ' +
  'fight fit fix forget forgive gather guess hide hire hit ' +
  'hurt improve introduce invent invite judge manage mark mean measure ' +
  'mention mind miss mix notice offer permit pick plan prepare ' +
  'present prevent produce promise protect provide publish pull push ' +
  'raise reach realize receive recognize record reduce refuse relax remain ' +
  'remember remove repeat replace report require reserve respect return rise ' +
  'save search seem select sense separate serve settle share shut ' +
  'sign solve sound spend spread steal stick succeed suffer suggest ' +
  'supply support suppose surprise survive test throw cross translate travel ' +
  'treat trust understand unite visit vote wake warn waste weigh ' +
  'welcome wonder worry account action adult age advice agreement air animal ' +
  'army art article artist attention attitude audience author average award ' +
  'bank beach bill birth blood board boat body bone border ' +
  'bottle brain branch breath bridge building camera camp capital card ' +
  'career castle century chance chapter character charge chart check chest ' +
  'chief choice church circle citizen claim cloud coast code collection ' +
  'college community condition conflict congress contact content contract conversation copy ' +
  'corner course court credit crime culture customer damage danger ' +
  'death debt decision degree demand department design detail device diet ' +
  'direction director discussion disease district document dog dollar dream dress ' +
  'drug duty earth east economy education effect effort election element ' +
  'emergency emotion employee employer energy engine environment equipment error event ' +
  'evidence example exchange expert factor farm fashion fear feature fee ' +
  'field figure finger fire fish flight floor flower focus foot ' +
  'force forest fortune frame freedom fruit function future garden gate ' +
  'gift goal gold grade grass ground growth guard guest guide ' +
  'gun hair hall handle happiness health heart heat history hobby ' +
  'hole holiday honor horse host hotel husband ice industry injury ' +
  'insect instrument insurance interest invention issue item journey joy key ' +
  'kitchen knowledge labor lake language law lawyer leader leaf library ' +
  'light limit list literature loss luck machine magazine mail manager ' +
  ''
).trim().split(/\s+/);

// ---------------------------------------------------------------------------
// B1（常见抽象词与四级中频词，能读懂一般说明文与新闻标题）
// ---------------------------------------------------------------------------

const B1_WORDS = (
  'achieve achievement active admire admit advance affair affect afford agency agent ' +
  'aim alarm alive alliance ambition ancient angle annual anxious apart apology ' +
  'appeal appearance application apply appreciate approach appropriate approve arrange arrest artificial ' +
  'aspect assist assume attach attack attempt attend attract authority automatic aware ' +
  'balance basis battle behave behavior belief benefit beyond bite blame block ' +
  'blow bond borrow bother bound brand brave brief bright broad budget ' +
  'burden calculate calm campaign cancel candidate capable capture cast category ceiling ' +
  'cell chain challenge champion channel characteristic charity chase cheat chemical circumstance ' +
  'civil classic client climate climb clue coal collapse combine comment commercial ' +
  'commission commit committee communicate compare compete complain complex concentrate concern conclude ' +
  'concrete confidence confirm confuse connect consider consist contain cope correct courage ' +
  'crash crisis criticize crowd cure curious current curve custom cycle dare ' +
  'data dawn debate declare decline defeat defend define delay delight deny ' +
  'desire despite determine devote digital disappoint disaster discover discuss display disturb ' +
  'domestic draft drag dramatic drift due dull eager ease economic edit ' +
  'educate effective efficient elect electronic embarrass employ enable encounter endure engage ' +
  'enormous ensure entertain enthusiasm equal equip escape especially establish estimate eventually ' +
  'exact exaggerate exam excite excuse executive exhaust expand expense experiment explode ' +
  'export extend extent external extra extreme facility faith familiar fancy fantasy ' +
  'fatal favor favorite fierce file finance firm flavor flexible float flood ' +
  'flow fold folk fond forbid forecast formal former formula found foundation ' +
  'frequent fresh frighten frontier fuel fulfill fund furniture gain gap gaze ' +
  'gentle glance globe glory govern grab gradual grain grant grasp grateful ' +
  'greet grief grocery guarantee guilt habit harm harvest hazard heal hesitate ' +
  'highlight hint honest horizon hostile household huge humble humor hunt hurry ' +
  'identify ignore imagine imitate immediate immigrant import impress impression income independent ' +
  'indirect individual industrial influence inform initial initiative injure inner innocent inquire ' +
  'insert insist inspire install instance instant instruct intelligent intend intention interaction ' +
  'interfere internal internet interrupt interval intimate invade invest investment involve iron ' +
  'island joint junior keen label lack landscape latest latter launch layer ' +
  'lazy lead league lean leap legal legend leisure lend liberty license ' +
  'likely link liquid loan locate logical loose lord loyal luxury manufacture ' +
  'mass master match means meanwhile mechanic melt merchant mercy mere mess ' +
  'metal mild military mineral minister minor miracle mission mobile moderate modest ' +
  'moral motion motor mount murder muscle mystery naked narrow nation native ' +
  'navy neat negative neglect nerve nervous noble nod normal novel nowhere ' +
  'nuclear numerous obey observe obstacle occasion occur odd offend official operate ' +
  'oppose option ordinary organize origin otherwise ought outer output outstanding overcome ' +
  'owe pace pack panic parallel particular passion passive path patience pause ' +
  'perform permanent personal persuade philosophy physical pile pioneer pitch pity plenty ' +
  'plot politician poll portrait possess postpone poverty practical pray pregnant prejudice ' +
  'presence preserve pretend pride print prior privilege procedure profession professional professor ' +
  'profile propose protest prove psychology punish purchase pure purple puzzle qualify ' +
  ''
).trim().split(/\s+/);

// ---------------------------------------------------------------------------
// B2（四六级核心与常见学术/商务词，能读懂论述文与评论）
// ---------------------------------------------------------------------------

const B2_WORDS = (
  'react recent recover refer reflect reform regard region regular ' +
  'relate relative release rely remind represent resist respond reward ruin ' +
  'rough routine sample satisfy scale schedule scientific seek senior sensitive ' +
  'serious severe shift shock shoot signal similar sketch situation smart ' +
  'smooth social soft solid spare split spot staff steady stock ' +
  'strange stretch strike submit substance sudden suggestion suitable superior survey ' +
  'suspect target technical tend tendency terrible thorough tight tiny tough ' +
  'trace track tragedy trial trick triumph tropical tune typical unique ' +
  'urban urge urgent usual vacant vague valuable vast victim violent ' +
  'visible vision visitor volunteer wander weapon wide willing wisdom witness ' +
  'wound zone quit quote random range rank rapid rare reality ' +
  'recall reception reckon reconcile recruit refund refugee register regret relief ' +
  'remark remarkable renew rescue resemble resign retire retreat revenge revive ' +
  'revolve ridge rifle riot ritual robust rotate rotten rubbish rugged rumor ' +
  'rush sack sacred saddle salute sanction satellite satire savage scan scandal ' +
  'scar scarce scatter scold scrap scrape scratch scrub scrutiny sculpture seal ' +
  'abolish abrupt accord acute agenda aggressive alien allowance amid apparatus ' +
  'appliance applicable arena arrogant assumption assurance astonish auction audit authorize ' +
  'aviation ballot bankrupt barren batch beverage bizarre blank blast blend ' +
  'blink bloom blunt blur boycott breach breakthrough brink brisk brochure ' +
  'broker bureaucracy bypass calorie caption carve census cereal certify charter ' +
  'chin chip choke chop circulate cite civilization classify clay clerk ' +
  'cliff clinic clock cloth cluster coach collar colony column combat ' +
  'comedy comfort command commerce companion compose composition compound comprehend compromise ' +
  'compulsory concept conquer conscience consent conservation continual cooperation copper cord ' +
  'costume cottage council counter courtesy crack craft crawl cream creep ' +
  'crew cruel cruise crystal curl curse cushion dam damp dash ' +
  'deaf decay deceive decent decorate defect defensive definite delicate democracy ' +
  'dense deputy descend despise destination destiny detect detective diagram dialect ' +
  'diameter dictate digest dimension diplomatic discount discourse disgrace disguise disgust ' +
  'dismantle dismay dispatch disperse disposal dispute dissipate distract distress distribute ' +
  'divert donate dread drought dubious duplicate dwell eclipse ecology economical ' +
  'edible elevate eligible eloquence embargo eminent emit encompass endanger endeavor ' +
  'endorse energetic engagement enrich entail epidemic erode escalate essence esteem ' +
  'eternal evacuate excel exceptional exempt exhibit expertise expire fabricate fake ' +
  'fare fatigue famine ferry fertile fiber flaw flee fluent forsake ' +
  'fortify fraud friction fury fuse galaxy generalize gloomy glorious gratitude ' +
  'greedy guideline guilty halt harmony harsh haste heritage hinder homogeneous ' +
  'honorable horizontal humidity hygiene ignite ignorant imminent impair impart imperative ' +
  'indifferent indulge infect inferior infinite inflation inflict influential infrastructure inhabit ' +
  'inhibit inject inquiry institution insult intact intake integral intellectual interior ' +
  'intermediate intrigue inventory invisible jealous jeopardize journal judgment junction justice ' +
  'laser laundry lawn leak lease legislation lengthy lessen liable lifetime ' +
  'literary litter lodge logic longevity lucrative lump magnificent majority manifest ' +
  'manual manuscript masterpiece maximum mental minority mislead moisture molecule momentum ' +
  'monopoly mortgage multiply municipal narrative navigate nitrogen nonetheless norm notable ' +
  ''
).trim().split(/\s+/);

// ---------------------------------------------------------------------------
// C1（考研 / 雅思托福偏难词：偏正式、偏学术的抽象表达）
// ---------------------------------------------------------------------------

const C1_WORDS = (
  'abandon abstract absurd abundant accelerate accessible accommodate accompany accomplish accumulate ' +
  'accurate acknowledge acquire adapt adequate adhere adjacent adopt adverse advocate ' +
  'aesthetic aggregate alleviate allocate allude alter alternative ambiguous amend amplify ' +
  'analogy anonymous anticipate apparent approximate arbitrary articulate aspire assert assess ' +
  'asset assign assimilate attain attribute authentic autonomy beneficial bias boost ' +
  'brutal bulk capacity casual catastrophe cease celebrity coherent coincide collaborate ' +
  'commence commodity compatible compel compensate competent compile complement comply component ' +
  'comprehensive comprise conceal concede conceive concise condemn confer confess confine ' +
  'conform confront conscious consensus consequence conserve considerable consistent constitute constrain ' +
  'consult consume contemporary contempt contend context contradict contrary controversy convention ' +
  'convert convey convict convince cooperate coordinate correspond corrupt credible criterion ' +
  'crucial cultivate cumulative currency curriculum cynical decade deduce deficit deliberate ' +
  'demonstrate denote depict deprive derive designate deteriorate deviate devise diagnose ' +
  'differentiate diminish discard discipline disclose discrepancy discrete discriminate dismiss displace ' +
  'dispose disrupt distinct distinguish distort diverse dominate drastic duration dynamic ' +
  'elaborate eliminate emerge emphasis empirical enact enhance enterprise entitle equivalent ' +
  'erupt essential ethical evaluate evolve exceed exception excess exclude exclusive ' +
  'explicit exploit expose extract facilitate faculty feasible federal fluctuate formulate ' +
  'foster framework fundamental generate genuine hierarchy hypothesis identical ideology ignorance ' +
  'illustrate immune impact implement imply impose incentive incident incline incorporate ' +
  'indicate induce inevitable infer inherent inherit initiate innovate insight inspect ' +
  'instinct institute integrate integrity intense interact interpret intervene intrinsic investigate ' +
  'invoke isolate justify legitimate magnitude maintain manipulate margin mature mechanism ' +
  'mediate medium merge methodology migrate minimize modify monitor motive mutual ' +
  'negotiate neutral nevertheless notion objective obligation obscure obtain occupy offset ' +
  'ongoing outcome overlap paradigm parameter participate perceive persist perspective phase ' +
  'phenomenon portion potential precede precise predict predominant preliminary presume prevail ' +
  'previous primary principle priority proceed prohibit prominent promote proportion prospect ' +
  'protocol pursue radical ratio rational refine regime regulate reinforce reject ' +
  'relevant reluctant remedy render replicate reside resolve resort restore restrain ' +
  'restrict retain reveal revenue reverse revise rigid route scenario scheme ' +
  'scope sequence significant simulate sole sophisticated spectrum speculate stable statistic ' +
  'stimulate strategy subsequent subsidy substantial substitute successive sufficient supplement sustain ' +
  'symbol synthesis systematic tackle tactic technique temporary tension terminate thesis ' +
  'threshold tolerate transfer transform transmit transparent trend trigger ultimate undergo ' +
  'underlying undertake utilize valid vanish variable vehicle version vertical violate ' +
  'virtual vital voluntary vulnerable welfare widespread yield ' +
  ''
).trim().split(/\s+/);

// ---------------------------------------------------------------------------
// C2（低频学术与文学词：只在考研进阶、原版文学与学术写作里常遇到）
// ---------------------------------------------------------------------------

const C2_WORDS = (
  'aberration acquiesce alacrity ambivalent anachronism antipathy apathetic arcane arduous ascetic ' +
  'assiduous austere banal bellicose cajole capricious caustic chicanery circumspect clandestine ' +
  'cogent commensurate compendium concomitant conflagration connoisseur contrite convivial copious crass ' +
  'cryptic cursory dearth debacle decorum deference deleterious desiccate desultory diatribe ' +
  'didactic diffident dilatory disabuse disparate dissemble ebullient eclectic egregious elusive ' +
  'emaciated emulate enervate enigmatic ennui ephemeral epitome equivocate esoteric exacerbate ' +
  'exhort exigent exonerate expedient extant extol extraneous extricate fastidious fervent ' +
  'flippant florid foment forbearance fortuitous frugal garrulous germane gregarious hackneyed ' +
  'halcyon hapless harbinger hegemony hyperbole iconoclast idiosyncrasy ignominious immutable impasse ' +
  'impeccable imperious imperturbable impervious implacable importune impromptu impunity inadvertent incendiary ' +
  ''
).trim().split(/\s+/);

/**
 * 按等级分层的**有序**词表（每个数组内部按使用频率大致从高到低）。
 *
 * 为什么额外导出：`WORD_CEFR_BAND` 只回答「这个词属于哪一级」，但调用方做更细的排序时
 * 需要词在分层数组中的**位置**（越靠前越基础）。这里顺序与构建 `WORD_CEFR_BAND` 时的
 * 顺序完全一致，调用方可自行建索引，不要在本文件里预先算好排名对象——
 * 那样会让「顺序」这一唯一真源出现两份。
 */
export const WORD_BANDS_ORDERED: Readonly<Record<CefrLevel, readonly string[]>> = {
  A1: A1_WORDS,
  A2: A2_WORDS,
  B1: B1_WORDS,
  B2: B2_WORDS,
  C1: C1_WORDS,
  C2: C2_WORDS,
};

/**
 * 把分层数组折叠成 lemma → 等级的查表对象。
 *
 * 取舍：同名 lemma 若在多个等级出现（人工维护时难免），按 `CEFR_LEVELS` 的顺序
 * **先到先得**（低等级优先）。理由是低等级列表本身按频率排序，先出现的通常更基础；
 * 而且静默覆盖比抛错更适合一个「近似分级」表——线上不该因为一个词重复就崩掉预测接口。
 * 重复本身由 `WORD_BAND_COUNTS` 与数据审查环节发现，不在运行时兜底。
 */
function buildBandMap(
  bands: Readonly<Record<CefrLevel, readonly string[]>>,
): Record<string, CefrLevel> {
  const map: Record<string, CefrLevel> = {};
  for (const level of CEFR_LEVELS) {
    for (const word of bands[level]) {
      if (map[word] === undefined) {
        map[word] = level;
      }
    }
  }
  return map;
}

/** 统计每个等级实际收录的词数（数据体积小，启动时算一次即可）。 */
function buildBandCounts(
  bands: Readonly<Record<CefrLevel, readonly string[]>>,
): Record<CefrLevel, number> {
  const counts = {} as Record<CefrLevel, number>;
  for (const level of CEFR_LEVELS) {
    counts[level] = bands[level].length;
  }
  return counts;
}

/** lemma（小写）→ CEFR 等级。模块加载时由上面的分层数组构建。 */
export const WORD_CEFR_BAND: Readonly<Record<string, CefrLevel>> = buildBandMap(WORD_BANDS_ORDERED);

/** 每个等级的收录词数（用于置信度与覆盖率计算）。 */
export const WORD_BAND_COUNTS: Readonly<Record<CefrLevel, number>> = buildBandCounts(WORD_BANDS_ORDERED);

/**
 * 查询一个词属于哪个等级；未收录返回 null（调用方据此走「超纲词」逻辑）。
 *
 * 纯内存对象查表：调用前先 `trim().toLowerCase()` 归一化，因此
 * `' Government '`、`'GOVERNMENT'` 与 `'government'` 得到同一结果。
 * 空串（或纯空白）返回 null，避免把空 token 误判成超纲词。
 */
export function lookupWordBand(lemma: string): CefrLevel | null {
  const key = lemma.trim().toLowerCase();
  if (key === '') {
    return null;
  }
  return WORD_CEFR_BAND[key] ?? null;
}
